import type { DeviceResponseV5 } from '@emma/api_client/types';
import {
  cpuArchToOsArch,
  getCompatibleOsVersionsForDevice,
  supportsNvidiaOpen,
} from '@emma/core/device';
import { InlineNotification, Link } from '@lego/klik-ui';
import { Link as ReactRouterLink } from 'react-router-dom';
import type { InstallerRow } from '@/components/common/table/column_definitions/OsInstaller';
import { writeTableStateToUrl } from '@/components/common/table/store/tableUrlParams';
import { useOsInstallers } from '@/hooks/os';
import { paths } from '@/utility/global_paths';

export const getCompatibleInstaller = (
  deviceType: DeviceResponseV5['deviceType'],
  installers: InstallerRow[]
) => {
  if (!deviceType) return undefined;
  const needsNvidia = supportsNvidiaOpen(deviceType.gpu ?? undefined);

  const candidates = installers.filter(
    (installer) =>
      installer.architecture === cpuArchToOsArch[deviceType.architecture] &&
      (needsNvidia ? installer.variant.startsWith('nvidia-open') : installer.variant === 'base')
  );
  return candidates
    .filter(
      (installer) =>
        getCompatibleOsVersionsForDevice(deviceType, [
          `${installer.version}-${installer.architecture}-${installer.variant}`,
        ]).length > 0
    )
    .sort(
      (a, b) =>
        b.version.localeCompare(a.version, undefined, { numeric: true }) ||
        b.variant.localeCompare(a.variant, undefined, { numeric: true })
    )[0];
};

export const DeviceInstallation = ({
  device,
}: {
  device: Pick<DeviceResponseV5, 'deviceType'>;
}) => {
  const { data } = useOsInstallers(
    device.deviceType ? { architecture: cpuArchToOsArch[device.deviceType.architecture] } : {},
    { showForbiddenErrorToast: false }
  );
  const installer = getCompatibleInstaller(device.deviceType, data?.data.installers ?? []);
  const params = installer
    ? writeTableStateToUrl(
        new URLSearchParams(),
        'OsInstallers',
        [],
        { pageIndex: 0, pageSize: 10 },
        [
          { id: 'version', value: installer.version },
          { id: 'architecture', value: installer.architecture },
          { id: 'variant', value: installer.variant },
        ]
      )
    : undefined;

  return (
    <InlineNotification mb={6} variant="info">
      <InlineNotification.Content>
        <InlineNotification.Title>Next step: install EMMA OS</InlineNotification.Title>
        <InlineNotification.Description>
          This device is registered but has not been provisioned. Flash a compatible EMMA OS
          installer and connect the device to the network. Provisioning keeps this Device ID.{' '}
          <Link
            as={ReactRouterLink as never}
            to={params ? `${paths.osDownloads}?${params}` : paths.osDownloads}
          >
            {installer
              ? `EMMA OS ${installer.version} installer (${installer.architecture}, ${installer.variant})`
              : 'Browse OS installers'}
          </Link>
          {!installer && ' — confirm compatibility with your device type before flashing.'}
        </InlineNotification.Description>
      </InlineNotification.Content>
    </InlineNotification>
  );
};
